import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  AutoscaleInfo,
  IChartApi,
  ISeriesApi,
  LogicalRange,
  MouseEventParams,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';
import {
  WORKLOAD_BIN_ORIGIN,
  WORKLOAD_TF_SECONDS,
  type WorkloadSeriesPoints,
  type WorkloadTf,
} from '../../api/dashboard';
import { formatDashboardMillions, formatDashboardNumber, formatDashboardPercent } from './formatters';
import type { WorkloadChartMode } from './WorkloadTimeframeBar';

export type WorkloadChartSeries = {
  id: string;
  name: string;
  color: string;
  visible: boolean;
  emphasis?: boolean;
};

type Props = {
  series: WorkloadChartSeries[];
  data: Record<string, WorkloadSeriesPoints>;
  windowFrom: number;
  windowTo: number;
  tf: WorkloadTf;
  metric: 'amount_vnd' | 'request_count';
  average?: boolean;
  mode?: WorkloadChartMode;
  conversionLive?: Record<string, { c: number; p: number }>;
  /** Changes whenever the accumulated history must be thrown away and redrawn. */
  resetKey: string;
  formatValue: (value: number) => string;
  loadingHistory?: boolean;
  onLoadHistory?: () => void;
  onToggleSeries?: (id: string, mode?: 'toggle' | 'solo') => void;
};

const CHART_HEIGHT = 340;
/** Ask for more history only when the left edge is almost on-screen. */
const HISTORY_TRIGGER_BARS = 8;
const LIVE_CONVERSION_WINDOW_SEC = 30 * 60;

const mskAxisTime = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const mskAxisDate = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: 'short',
});
const mskFull = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function withAlpha(hex: string, alpha: number): string {
  const raw = hex.replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Bucket boundaries follow the server's date_bin origin (MSK midnight). */
function alignBucket(ts: number, stepSec: number): number {
  return WORKLOAD_BIN_ORIGIN + Math.floor((ts - WORKLOAD_BIN_ORIGIN) / stepSec) * stepSec;
}

/** Zero / missing buckets are whitespace so the line does not sit on the X-axis. */
function toLineData(times: number[], values: Array<number | null>) {
  return times.map((time, index) => {
    const value = values[index];
    if (value == null || value === 0) return { time: time as UTCTimestamp };
    return { time: time as UTCTimestamp, value };
  });
}

function seriesTotals(
  points: WorkloadSeriesPoints | undefined,
  fromSec?: number,
  toSec?: number,
  bucketSec?: number,
): {
  rub: number;
  vnd: number;
  count: number;
  paid: number;
} {
  if (!points) return { rub: 0, vnd: 0, count: 0, paid: 0 };
  const clip = fromSec != null && toSec != null;
  const step = bucketSec && bucketSec > 0 ? bucketSec : 0;
  let vnd = 0;
  let rub = 0;
  let count = 0;
  let paid = 0;
  for (let i = 0; i < points.t.length; i += 1) {
    if (clip) {
      const ts = Number(points.t[i] ?? 0) || 0;
      if (step > 0) {
        if (ts + step <= fromSec || ts >= toSec) continue;
      } else if (ts < fromSec || ts >= toSec) {
        continue;
      }
    }
    vnd += Number(points.v[i] ?? 0) || 0;
    rub += Number(points.r?.[i] ?? 0) || 0;
    count += Number(points.c[i] ?? 0) || 0;
    paid += Number(points.p?.[i] ?? 0) || 0;
  }
  return { rub, vnd, count, paid };
}

function seriesConversionPercent(
  points: WorkloadSeriesPoints | undefined,
  fromSec?: number,
  toSec?: number,
  bucketSec?: number,
): number | null {
  const totals = seriesTotals(points, fromSec, toSec, bucketSec);
  if (totals.count <= 0) return null;
  return (totals.paid / totals.count) * 100;
}

function seriesDisplayTotals(
  points: WorkloadSeriesPoints | undefined,
  average: boolean,
): { rub: number; vnd: number } {
  const totals = seriesTotals(points);
  if (!average || totals.count <= 0) return { rub: totals.rub, vnd: totals.vnd };
  return { rub: totals.rub / totals.count, vnd: totals.vnd / totals.count };
}

function formatExactAmount(value: number): string {
  return formatDashboardNumber(value, Number.isInteger(value) ? 0 : 2);
}

function conversionAutoscale(): AutoscaleInfo {
  return { priceRange: { minValue: 0, maxValue: 100 } };
}

/** Floor is always 0 — the X-axis. Top follows the visible data. */
function workloadAutoscale(original: () => AutoscaleInfo | null): AutoscaleInfo {
  const res = original();
  const maxValue = Math.max(res?.priceRange?.maxValue ?? 0, 0);
  return {
    priceRange: {
      minValue: 0,
      maxValue: maxValue > 0 ? maxValue : 1,
    },
  };
}

export function WorkloadTimeSeriesChart({
  series,
  data,
  windowFrom,
  windowTo,
  tf,
  metric,
  average = false,
  mode = 'conversion',
  conversionLive,
  resetKey,
  formatValue,
  loadingHistory = false,
  onLoadHistory,
  onToggleSeries,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const lwcRef = useRef<typeof import('lightweight-charts') | null>(null);
  const seriesApiRef = useRef(new Map<string, ISeriesApi<'Line'>>());
  const appliedRef = useRef<{ resetKey: string; view: string; times: number[] } | null>(null);
  const loadHistoryRef = useRef(onLoadHistory);
  const formatValueRef = useRef(formatValue);
  const formatAxisRef = useRef(formatValue);
  const [ready, setReady] = useState(false);
  const seriesIds = series.map((item) => item.id).join('\0');
  const [focusId, setFocusId] = useState<string | null>(null);
  const [hover, setHover] = useState<{
    x: number;
    y: number;
    time: number;
    rows: Array<{ id: string; name: string; color: string; value: number }>;
  } | null>(null);

  loadHistoryRef.current = onLoadHistory;
  formatValueRef.current = formatValue;
  formatAxisRef.current = (value: number) => {
    if (mode === 'conversion') return formatDashboardPercent(value);
    if (average) {
      return Math.abs(value) >= 1_000_000
        ? formatDashboardMillions(value)
        : formatDashboardNumber(value, 0);
    }
    return metric === 'amount_vnd' ? formatDashboardMillions(value) : formatValue(value);
  };
  const viewKey = `${mode}|${metric}|${average ? 'avg' : 'sum'}`;
  const conversion = mode === 'conversion';

  const stepSec = WORKLOAD_TF_SECONDS[tf];
  const legendTo = Math.min(windowTo, Math.floor(Date.now() / 1000) + 1);
  const legendFrom = Math.max(windowFrom, legendTo - stepSec);
  const tfShort =
    tf === '1m' ? '1м' : tf === '5m' ? '5м' : tf === '15m' ? '15м' : tf === '30m' ? '30м' : tf === '1h' ? '1ч' : tf === '4h' ? '4ч' : '1д';

  /** Dense grid: sparse server buckets expanded with explicit zeros.
   *  `windowTo` is an exclusive unix bound. Never draw buckets that have not started. */
  const grid = useMemo(() => {
    const start = alignBucket(windowFrom, stepSec);
    const nowBucket = alignBucket(Math.floor(Date.now() / 1000), stepSec);
    const end = Math.min(alignBucket(windowTo - 1, stepSec), nowBucket);
    const count = end >= start ? Math.floor((end - start) / stepSec) + 1 : 0;
    if (count === 0) return { times: [] as number[], values: new Map<string, Array<number | null>>() };

    const times = new Array<number>(count);
    for (let i = 0; i < count; i += 1) times[i] = start + i * stepSec;

    const values = new Map<string, Array<number | null>>();
    for (const item of series) {
      const filled = new Array<number | null>(count).fill(null);
      const points = data[item.id];
      if (points) {
        const source = metric === 'amount_vnd' ? points.v : points.c;
        for (let i = 0; i < points.t.length; i += 1) {
          const index = Math.floor((points.t[i] - start) / stepSec);
          if (index < 0 || index >= count) continue;
          const requests = Number(points.c[i] ?? 0) || 0;
          if (conversion) {
            if (requests <= 0) continue;
            filled[index] = ((Number(points.p?.[i] ?? 0) || 0) / requests) * 100;
            continue;
          }
          const raw = Number(source[i] ?? 0) || 0;
          if (!average) {
            filled[index] = raw;
            continue;
          }
          if (requests <= 0) continue;
          const sum =
            metric === 'amount_vnd'
              ? Number(points.v[i] ?? 0) || 0
              : Number(points.r?.[i] ?? 0) || 0;
          filled[index] = sum / requests;
        }
      }
      if (conversion && conversionLive && stepSec === LIVE_CONVERSION_WINDOW_SEC && times[count - 1] === nowBucket) {
        const live = conversionLive[item.id];
        const requests = Number(live?.c ?? 0) || 0;
        filled[count - 1] = requests > 0 ? ((Number(live?.p ?? 0) || 0) / requests) * 100 : null;
      }
      values.set(item.id, filled);
    }
    return { times, values };
  }, [series, data, windowFrom, windowTo, stepSec, metric, average, conversion, conversionLive]);

  const seriesMeta = useMemo(() => {
    const map = new Map<string, WorkloadChartSeries>();
    for (const item of series) map.set(item.id, item);
    return map;
  }, [series]);
  const seriesMetaRef = useRef(seriesMeta);
  seriesMetaRef.current = seriesMeta;

  useEffect(() => {
    let disposed = false;
    const container = containerRef.current;
    if (!container) return;

    void (async () => {
      const lwc = await import('lightweight-charts');
      if (disposed || !containerRef.current) return;

      const chart = lwc.createChart(containerRef.current, {
        autoSize: true,
        layout: {
          background: { color: 'transparent' },
          textColor: '#64748b',
          fontSize: 11,
          fontFamily: 'inherit',
          attributionLogo: false,
        },
        grid: {
          vertLines: { color: '#f1f5f9' },
          horzLines: { color: '#f1f5f9' },
        },
        rightPriceScale: {
          borderColor: '#e2e8f0',
          autoScale: true,
          scaleMargins: { top: 0, bottom: 0 },
        },
        handleScale: {
          axisPressedMouseMove: { time: true, price: false },
        },
        handleScroll: {
          vertTouchDrag: false,
        },
        crosshair: {
          mode: lwc.CrosshairMode.Normal,
          vertLine: { color: '#94a3b8', width: 1, style: lwc.LineStyle.Dotted, labelBackgroundColor: '#334155' },
          horzLine: { color: '#94a3b8', width: 1, style: lwc.LineStyle.Dotted, labelBackgroundColor: '#334155' },
        },
        timeScale: {
          borderColor: '#e2e8f0',
          timeVisible: true,
          secondsVisible: false,
          rightOffset: 0,
          fixRightEdge: true,
          tickMarkFormatter: (time: Time, tickMarkType: number) => {
            const ms = Number(time) * 1000;
            return tickMarkType >= lwc.TickMarkType.Time ? mskAxisTime.format(ms) : mskAxisDate.format(ms);
          },
        },
        localization: {
          locale: 'ru-RU',
          priceFormatter: (price: number) => formatAxisRef.current(price),
          timeFormatter: (time: Time) => mskFull.format(Number(time) * 1000),
        },
      });

      chart.subscribeCrosshairMove((param: MouseEventParams) => {
        if (!param.point || param.time == null || param.seriesData.size === 0) {
          setHover(null);
          return;
        }
        const rows: Array<{ id: string; name: string; color: string; value: number }> = [];
        for (const [id, api] of seriesApiRef.current) {
          const meta = seriesMetaRef.current.get(id);
          if (!meta || !meta.visible) continue;
          const point = param.seriesData.get(api) as { value?: number } | undefined;
          if (!point || typeof point.value !== 'number') continue;
          rows.push({ id, name: meta.name, color: meta.color, value: point.value });
        }
        rows.sort((a, b) => b.value - a.value);
        setHover({ x: param.point.x, y: param.point.y, time: Number(param.time), rows });
      });

      chart.timeScale().subscribeVisibleLogicalRangeChange((range: LogicalRange | null) => {
        if (!range) return;
        const first = seriesApiRef.current.values().next().value;
        if (!first) return;
        const bars = first.barsInLogicalRange(range);
        if (bars && bars.barsBefore < HISTORY_TRIGGER_BARS) loadHistoryRef.current?.();
      });

      lwcRef.current = lwc;
      chartRef.current = chart;
      setReady(true);
    })();

    return () => {
      disposed = true;
      chartRef.current?.remove();
      chartRef.current = null;
      lwcRef.current = null;
      seriesApiRef.current.clear();
      appliedRef.current = null;
      setReady(false);
    };
  }, []);

  // Create/remove one line series per profile. Must be sync: an async import here
  // raced the data effect, so the first paint stayed empty until a TF click.
  useEffect(() => {
    const chart = chartRef.current;
    const lwc = lwcRef.current;
    if (!chart || !lwc || !ready) return;
    const apis = seriesApiRef.current;
    const wanted = new Set(series.map((item) => item.id));

    for (const [id, api] of apis) {
      if (wanted.has(id)) continue;
      chart.removeSeries(api);
      apis.delete(id);
    }
    for (const item of series) {
      if (apis.has(item.id)) continue;
      apis.set(
        item.id,
        chart.addSeries(lwc.LineSeries, {
          color: item.color,
          lineWidth: item.emphasis ? 3 : 2,
          lineType: lwc.LineType.Simple,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerRadius: 3,
          priceFormat: {
            type: 'custom',
            minMove: conversion ? 0.1 : 1,
            formatter: (price: number) => formatAxisRef.current(price),
          },
          autoscaleInfoProvider: conversion ? conversionAutoscale : workloadAutoscale,
        }),
      );
      appliedRef.current = null;
    }
  }, [series, seriesIds, ready, conversion]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !ready) return;
    const apis = seriesApiRef.current;
    if (apis.size === 0 || apis.size !== series.length) return;
    const { times, values } = grid;
    if (times.length === 0) return;

    const applied = appliedRef.current;
    const timeScale = chart.timeScale();
    const lastIndex = Math.max(0, times.length - 1);

    chart.applyOptions({
      localization: {
        priceFormatter: (price: number) => formatAxisRef.current(price),
      },
      rightPriceScale: {
        autoScale: true,
        scaleMargins: conversion ? { top: 0, bottom: 0 } : { top: 0.12, bottom: 0 },
      },
    });
    chart.priceScale('right').setAutoScale(true);

    for (const [id, api] of apis) {
      api.applyOptions({
        priceFormat: {
          type: 'custom',
          minMove: conversion ? 0.1 : 1,
          formatter: (price: number) => formatAxisRef.current(price),
        },
        autoscaleInfoProvider: conversion ? conversionAutoscale : workloadAutoscale,
      });
      api.setData(toLineData(times, values.get(id) ?? []));
    }

    if (!applied || applied.resetKey !== resetKey || applied.view !== viewKey) {
      timeScale.fitContent();
      appliedRef.current = { resetKey, view: viewKey, times };
      return;
    }

    const before = timeScale.getVisibleLogicalRange();
    const shift =
      applied.times[0] !== times[0] ? Math.max(0, times.indexOf(applied.times[0])) : 0;
    const added = Math.max(0, times.length - applied.times.length - shift);
    const followedRight = Boolean(before && before.to >= applied.times.length - 1.01);
    if (before) {
      if (shift > 0) {
        timeScale.setVisibleLogicalRange({
          from: before.from + shift,
          to: before.to + shift,
        });
      } else if (followedRight) {
        timeScale.setVisibleLogicalRange({
          from: before.from + added,
          to: lastIndex,
        });
      } else {
        timeScale.setVisibleLogicalRange(before);
      }
    }
    appliedRef.current = { resetKey, view: viewKey, times };
  }, [grid, seriesIds, series.length, resetKey, viewKey, ready, stepSec, conversion]);

  // Visibility and hover focus are option-level, so no data reupload is needed.
  useEffect(() => {
    if (!ready) return;
    for (const item of series) {
      const api = seriesApiRef.current.get(item.id);
      if (!api) continue;
      const dimmed = focusId != null && focusId !== item.id;
      api.applyOptions({
        visible: item.visible,
        color: dimmed ? withAlpha(item.color, 0.18) : item.color,
        lineWidth: focusId === item.id ? 3 : 2,
      });
    }
  }, [series, focusId, ready]);

  const hasSeries = series.length > 0;

  return (
    <div className="flex min-w-0 gap-3">
      <div className="relative min-w-0 flex-1">
        <div
          ref={containerRef}
          className="w-full"
          style={{ height: CHART_HEIGHT }}
          role="img"
          aria-label={conversion ? 'Конверсия реквизитов' : 'Загруженность реквизитов'}
        />

        {!hasSeries ? (
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/70 text-xs font-medium text-slate-400">
            Нет данных за выбранный период
          </div>
        ) : null}

        {loadingHistory ? (
          <div className="pointer-events-none absolute left-2 top-2 rounded-md bg-slate-900/80 px-2 py-1 text-[10px] font-semibold text-white">
            Загрузка истории…
          </div>
        ) : null}

        {hover && hover.rows.length > 0 ? (
          <div
            className="pointer-events-none absolute z-20 max-w-[260px] rounded-lg bg-slate-900/95 px-2.5 py-2 text-[11px] shadow-lg"
            style={{
              left: Math.min(Math.max(hover.x + 14, 0), Math.max(0, (containerRef.current?.clientWidth ?? 0) - 270)),
              top: Math.min(Math.max(hover.y - 12, 0), Math.max(0, CHART_HEIGHT - 180)),
            }}
          >
            <div className="mb-1 font-bold text-white">{mskFull.format(hover.time * 1000)}</div>
            <div className="vnd-scroll max-h-40 space-y-0.5 overflow-y-auto">
              {hover.rows.map((row) => (
                <div key={row.id} className="flex items-center justify-between gap-3 text-white">
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ backgroundColor: row.color }}
                    />
                    <span className="truncate font-medium">{row.name}</span>
                  </span>
                  <span className="shrink-0 font-mono font-semibold">{formatValue(row.value)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="vnd-scroll hidden w-[20.5rem] shrink-0 flex-col overflow-y-auto sm:flex sm:max-h-[21rem]">
        <div className="mb-0.5 flex items-center gap-1.5 px-1.5 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
          <span className="h-2 w-2 shrink-0" />
          <span className="min-w-0 flex-1">Профиль</span>
          {conversion ? (
            <span className="w-[5.25rem] shrink-0 text-right">конв. {tfShort}</span>
          ) : (
            <>
              <span className="w-[4.75rem] shrink-0 text-right">{average ? 'ср. ₽' : '₽'}</span>
              <span className="w-[6rem] shrink-0 text-right">{average ? 'ср. VND' : 'VND'}</span>
            </>
          )}
        </div>
        {series.map((item) => {
          const muted = !item.visible;
          const focused = focusId === item.id;
          const totals = seriesDisplayTotals(data[item.id], average);
          const conv = seriesConversionPercent(data[item.id], legendFrom, legendTo, stepSec);
          return (
            <button
              key={item.id}
              type="button"
              onClick={(event) => {
                const solo = event.altKey || event.metaKey || event.ctrlKey;
                onToggleSeries?.(item.id, solo ? 'solo' : 'toggle');
              }}
              onMouseEnter={() => {
                if (item.visible) setFocusId(item.id);
              }}
              onMouseLeave={() => setFocusId(null)}
              className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[11px] font-medium transition ${
                muted
                  ? 'text-slate-400 line-through opacity-60'
                  : focused
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-700 hover:bg-slate-50'
              }`}
              title={
                conversion
                  ? `${item.name}\n${conv == null ? 'нет заявок' : formatDashboardPercent(conv)}\nКлик — скрыть/показать\n⌥ / Ctrl — только этот`
                  : `${item.name}\n${formatExactAmount(totals.rub)} ₽ · ${formatExactAmount(totals.vnd)} VND\nКлик — скрыть/показать\n⌥ / Ctrl — только этот`
              }
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: muted ? '#cbd5e1' : item.color }}
              />
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              {conversion ? (
                <span className="w-[5.25rem] shrink-0 text-right font-mono text-[10px] font-semibold tabular-nums">
                  {conv == null ? '—' : formatDashboardPercent(conv)}
                </span>
              ) : (
                <>
                  <span className="w-[4.75rem] shrink-0 text-right font-mono text-[10px] font-semibold tabular-nums">
                    {formatExactAmount(totals.rub)}
                  </span>
                  <span className="w-[6rem] shrink-0 text-right font-mono text-[10px] font-semibold tabular-nums">
                    {formatExactAmount(totals.vnd)}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
